// Rebirth cutscene — the legendary moment a player picks the Rebirth card.
//
// Mythological frame: death → dissolution → descent → light → lotus → word.
// Pulls from desert iconography common to Christian (passion/resurrection,
// halo, beam) and Buddhist (samsara wheel, lotus from mud, dissolution into
// emptiness). The unifying visual is a radiant disc that morphs from
// dharma wheel → sun halo → lotus bloom, all built from procedural
// primitives (no sprite assets).
//
// World-space layer (drawn under camera transform): inward-spiraling
// particles + dissolving silhouette around the player.
//
// Screen-space layer (drawn over the world without camera): ochre tint,
// the radiant disc, the descending beam, the title cluster, the white
// flash that masks the actual state reset.

import { t } from '../i18n.js';
import { Vec2, randRange } from '../utils.js';

// Phase boundaries, in seconds since the cutscene started. Each entry
// names the end of the phase named on the line. End-only schedule keeps
// `phaseProgress(start, end)` two-arg readable below.
const T = {
  HUSH: 1.0,       // P1: time stops, player slumps, sepia creeps in
  DISSOLVE: 2.4,   // P2: silhouette sloughs into rising dust + wheel forms
  DESCENT: 3.6,    // P3: screen darkens, wheel rotates, low bell
  LIGHT: 4.6,      // P4: vertical golden beam, wheel → sun halo
  LOTUS: 6.0,      // P5: lotus opens, player rises within
  WORD: 7.0,       // P6: title + aphorism + tail
  // P7 split: title + letterbox unwind first (WORD → POST_ANIM), then
  // the camera zoom-out plays alone with nothing else moving. Two-stage
  // tail avoids the zoom motion competing with text fades.
  POST_ANIM: 7.5,
  END: 8.0,
};

export const REBIRTH_DURATION_SEC = T.END;

// Easing helpers. smoothstep is the standard symmetric S-curve; linRamp
// is just a clamped linear ramp used when we want hard edges.
function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
function smoothstep(a, b, t) {
  if (b <= a) return t < a ? 0 : 1;
  const x = clamp01((t - a) / (b - a));
  return x * x * (3 - 2 * x);
}
function linRamp(a, b, t) {
  if (b <= a) return t < a ? 0 : 1;
  return clamp01((t - a) / (b - a));
}

// World-space ember that drifts off the player's silhouette as he
// dissolves. Light upward bias and slight horizontal jitter; fades over
// life. Lives in its own list (not the main particles array) so the
// cutscene module owns its full lifecycle.
class DissolveEmber {
  constructor(x, y) {
    this.pos = new Vec2(x, y);
    this.vel = new Vec2(randRange(-8, 8), randRange(-50, -20));
    this.life = randRange(0.6, 1.1);
    this.maxLife = this.life;
    this.color = Math.random() < 0.5 ? '#ffd76a' : '#f4a72c';
    this.size = randRange(2, 3.5);
    this.alive = true;
  }
  update(dt) {
    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;
    // Gentle deceleration so they hang in the air, not whoosh off-screen.
    this.vel.x *= 0.96;
    this.vel.y *= 0.97;
    this.life -= dt;
    if (this.life <= 0) this.alive = false;
  }
  render(r) {
    const a = clamp01(this.life / this.maxLife);
    r.setAlpha(a);
    r.rect(
      Math.round(this.pos.x - this.size / 2),
      Math.round(this.pos.y - this.size / 2),
      Math.ceil(this.size),
      Math.ceil(this.size),
      this.color,
    );
    r.resetAlpha();
  }
}

// Parametric inward spiral. Particle is created at radius R0, swept
// inward over its lifetime, with angular velocity creating the curl.
// Using a parametric form (not velocity-driven) gives a clean visual
// spiral that doesn't depend on the integrator's step size.
class SpiralParticle {
  constructor(centerX, centerY, R0, theta0, omega, life) {
    this.cx = centerX;
    this.cy = centerY;
    this.R0 = R0;
    this.theta0 = theta0;
    this.omega = omega;
    this.life = life;
    this.maxLife = life;
    this.color = Math.random() < 0.7 ? '#ffd76a' : '#fff5d2';
    this.size = randRange(1.8, 3.0);
    this.alive = true;
  }
  update(dt) {
    this.life -= dt;
    if (this.life <= 0) this.alive = false;
  }
  render(r) {
    const u = 1 - this.life / this.maxLife; // 0 → 1
    const radius = this.R0 * (1 - u * u);   // accelerates inward at the end
    const theta = this.theta0 + this.omega * (this.maxLife - this.life);
    const x = this.cx + Math.cos(theta) * radius;
    const y = this.cy + Math.sin(theta) * radius;
    const a = 0.9 - 0.6 * u;
    r.setAlpha(a);
    r.rect(
      Math.round(x - this.size / 2),
      Math.round(y - this.size / 2),
      Math.ceil(this.size),
      Math.ceil(this.size),
      this.color,
    );
    r.resetAlpha();
  }
}

// Outward gold spark thrown at the resurrection moment.
class GoldEmber {
  constructor(x, y) {
    this.pos = new Vec2(x, y);
    const angle = Math.random() * Math.PI * 2;
    const speed = randRange(80, 220);
    this.vel = new Vec2(Math.cos(angle) * speed, Math.sin(angle) * speed);
    this.life = randRange(0.6, 1.2);
    this.maxLife = this.life;
    this.color = Math.random() < 0.5 ? '#ffd700' : '#fff2a8';
    this.size = randRange(2, 4);
    this.gravity = 60;
    this.alive = true;
  }
  update(dt) {
    this.vel.y += this.gravity * dt;
    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;
    this.life -= dt;
    if (this.life <= 0) this.alive = false;
  }
  render(r) {
    const a = clamp01(this.life / this.maxLife);
    r.setAlpha(a);
    r.rect(
      Math.round(this.pos.x - this.size / 2),
      Math.round(this.pos.y - this.size / 2),
      Math.ceil(this.size),
      Math.ceil(this.size),
      this.color,
    );
    r.resetAlpha();
  }
}

// Expanding ground ring drawn in WORLD space under the player at the
// light strike. Reads as a shockwave scorching the sand. Two concentric
// rings give a sense of layered impact.
class ShockRing {
  constructor(cx, cy) {
    this.cx = cx;
    this.cy = cy;
    this.life = 0.9;
    this.maxLife = 0.9;
    this.startR = 12;
    this.endR = 420;
    this.alive = true;
  }
  update(dt) {
    this.life -= dt;
    if (this.life <= 0) this.alive = false;
  }
  render(r) {
    const u = 1 - this.life / this.maxLife;
    const radius = this.startR + (this.endR - this.startR) * (1 - (1 - u) * (1 - u));
    const a = (1 - u) * 0.85;
    if (a < 0.01) return;
    r.setAlpha(a);
    r.strokeCircle(this.cx, this.cy, radius, '#ffd700', 5);
    r.strokeCircle(this.cx, this.cy, radius * 0.92, '#fff5d2', 2);
    r.resetAlpha();
  }
}

// Screen-space star with its own twinkle phase. Lives for the whole
// duration once spawned; the cutscene module owns alpha fades via phase.
class Star {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.size = Math.random() < 0.85 ? 1 : 2;
    this.phase = Math.random() * Math.PI * 2;
    this.speed = randRange(1.2, 3.5);
    this.baseA = randRange(0.45, 1.0);
  }
  render(r, time, masterA) {
    // Twinkle modulates between 0.4 and 1.0 of base alpha.
    const tw = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(this.phase + this.speed * time));
    const a = this.baseA * tw * masterA;
    if (a < 0.02) return;
    r.setAlpha(a);
    r.rect(this.x, this.y, this.size, this.size, '#ffffff');
    r.resetAlpha();
  }
}

export class RebirthCutscene {
  constructor() {
    this.reset(0);
  }

  reset(waveBefore) {
    this.t = 0;
    this.waveBefore = waveBefore;
    this.dissolveEmbers = [];
    this.spirals = [];
    this.goldEmbers = [];
    this.shocks = [];
    this.stars = [];
    this.finished = false;
    // Audio + camera cues fire exactly once each at their phase boundaries.
    this._cuedHeartbeat = false;
    this._cuedWhoosh = false;
    this._cuedRumble = false;
    this._cuedBell = false;
    this._cuedChoir = false;
    this._cuedShake = false;
    this._cuedShock = false;
    this._cuedStars = false;
    // Three mandala-ring angles integrated independently for counter-rotation.
    this._ringAngleOuter = 0;
    this._ringAngleMid = 0;
    this._ringAngleInner = 0;
    // Spawn pacing — cap per-frame emissions so very long dt steps don't
    // dump hundreds of particles at once.
    this._dissolveBudget = 0;
    this._spiralBudget = 0;
  }

  // Drive timer, emit particles, fire one-shot audio/camera cues.
  // `ctx` carries everything from Game: { player, camera, audio,
  // screenW, screenH }. Player may be null in degenerate test setups.
  update(dt, ctx) {
    this.t += dt;
    const t = this.t;

    // Three mandala rings rotate at different speeds in different
    // directions so the glyph reads as alive, not a flat decal. Inner
    // ring accelerates toward the light burst (samsara speeding up).
    const omegaBase = 0.4 + 1.6 * smoothstep(T.HUSH, T.LIGHT, t);
    this._ringAngleOuter += omegaBase * 0.6 * dt;
    this._ringAngleMid   -= omegaBase * 0.9 * dt;
    this._ringAngleInner += omegaBase * 1.5 * dt;

    // Heartbeat starts at the very first tick — death anxiety pattern.
    if (!this._cuedHeartbeat && ctx.audio && ctx.audio.playRebirthHeartbeat) {
      ctx.audio.playRebirthHeartbeat();
      this._cuedHeartbeat = true;
    }

    // P1 HUSH — gentle dust trickle off the player's feet.
    if (t < T.HUSH && ctx.player) {
      this._dissolveBudget += dt * 18;
      while (this._dissolveBudget >= 1) {
        this._dissolveBudget -= 1;
        const px = ctx.player.pos.x + randRange(-8, 8);
        const py = ctx.player.pos.y + randRange(-2, 8);
        this.dissolveEmbers.push(new DissolveEmber(px, py));
      }
    }

    // P2 DISSOLVE — silhouette sloughs; rate climbs through the phase.
    if (t >= T.HUSH && t < T.DISSOLVE && ctx.player) {
      const phase = linRamp(T.HUSH, T.DISSOLVE, t);
      this._dissolveBudget += dt * (40 + 80 * phase);
      while (this._dissolveBudget >= 1) {
        this._dissolveBudget -= 1;
        const px = ctx.player.pos.x + randRange(-10, 10);
        const py = ctx.player.pos.y + randRange(-14, 6);
        this.dissolveEmbers.push(new DissolveEmber(px, py));
      }

      // Begin inward spiral. Each spiral lives ~ to T.LIGHT so they all
      // converge near the singularity.
      this._spiralBudget += dt * (30 + 80 * phase);
      while (this._spiralBudget >= 1) {
        this._spiralBudget -= 1;
        const R0 = randRange(110, 220);
        const theta0 = Math.random() * Math.PI * 2;
        const omegaP = randRange(2.2, 4.5) * (Math.random() < 0.5 ? 1 : -1);
        const life = randRange(0.9, 1.6);
        this.spirals.push(new SpiralParticle(
          ctx.player.pos.x, ctx.player.pos.y, R0, theta0, omegaP, life,
        ));
      }

      if (!this._cuedWhoosh && ctx.audio && ctx.audio.playRebirthWhoosh) {
        ctx.audio.playRebirthWhoosh();
        this._cuedWhoosh = true;
      }
    }

    // P3 DESCENT — fewer new spirals, world goes dark, bell tolls,
    // rumble builds, starfield reveals behind the wheel.
    if (t >= T.DISSOLVE && t < T.DESCENT && ctx.player) {
      this._spiralBudget += dt * 40;
      while (this._spiralBudget >= 1) {
        this._spiralBudget -= 1;
        const R0 = randRange(60, 160);
        const theta0 = Math.random() * Math.PI * 2;
        const omegaP = randRange(3.0, 5.5) * (Math.random() < 0.5 ? 1 : -1);
        const life = randRange(0.6, 1.1);
        this.spirals.push(new SpiralParticle(
          ctx.player.pos.x, ctx.player.pos.y, R0, theta0, omegaP, life,
        ));
      }
      if (!this._cuedBell && ctx.audio && ctx.audio.playRebirthBell) {
        ctx.audio.playRebirthBell();
        this._cuedBell = true;
      }
      if (!this._cuedRumble && ctx.audio && ctx.audio.playRebirthRumble) {
        ctx.audio.playRebirthRumble();
        this._cuedRumble = true;
      }
      if (!this._cuedStars) {
        this._spawnStars(ctx.screenW || 1280, ctx.screenH || 720);
        this._cuedStars = true;
      }
    }

    // P4 LIGHT — beam strikes, sun halo blooms, camera shake on impact,
    // ground shockwaves expand from the player position.
    if (t >= T.DESCENT && t < T.LIGHT) {
      if (!this._cuedShake && ctx.camera) {
        ctx.camera.shake(12, 0.5);
        this._cuedShake = true;
      }
      if (!this._cuedChoir && ctx.audio && ctx.audio.playRebirthChoir) {
        ctx.audio.playRebirthChoir();
        this._cuedChoir = true;
      }
      if (!this._cuedShock && ctx.player) {
        // Three rings staggered by life offset so they read as
        // successive shockwaves, not one fat ring.
        const px = ctx.player.pos.x;
        const py = ctx.player.pos.y;
        for (let i = 0; i < 3; i++) {
          const ring = new ShockRing(px, py);
          ring.life = 0.9 + i * 0.15;
          ring.maxLife = ring.life;
          this.shocks.push(ring);
        }
        this._cuedShock = true;
      }
    }

    // P5 LOTUS — outward gold spray at the moment the player stands.
    if (t >= T.LIGHT && t < T.LOTUS && ctx.player) {
      const phase = linRamp(T.LIGHT, T.LOTUS, t);
      // Burst is concentrated in the first third of the lotus phase.
      if (phase < 0.35) {
        this._dissolveBudget += dt * 80;
        while (this._dissolveBudget >= 1) {
          this._dissolveBudget -= 1;
          this.goldEmbers.push(new GoldEmber(ctx.player.pos.x, ctx.player.pos.y));
        }
      }
    }

    // Tick particle lifetimes regardless of phase so old ones decay.
    for (let i = this.dissolveEmbers.length - 1; i >= 0; i--) {
      this.dissolveEmbers[i].update(dt);
      if (!this.dissolveEmbers[i].alive) this.dissolveEmbers.splice(i, 1);
    }
    for (let i = this.spirals.length - 1; i >= 0; i--) {
      this.spirals[i].update(dt);
      if (!this.spirals[i].alive) this.spirals.splice(i, 1);
    }
    for (let i = this.goldEmbers.length - 1; i >= 0; i--) {
      this.goldEmbers[i].update(dt);
      if (!this.goldEmbers[i].alive) this.goldEmbers.splice(i, 1);
    }
    for (let i = this.shocks.length - 1; i >= 0; i--) {
      this.shocks[i].update(dt);
      if (!this.shocks[i].alive) this.shocks.splice(i, 1);
    }

    if (t >= T.END) this.finished = true;
  }

  // One-shot screen-space starfield spawn at descent. Positions are
  // computed once and stored — twinkle alpha varies per render.
  _spawnStars(sw, sh) {
    const count = 110;
    for (let i = 0; i < count; i++) {
      this.stars.push(new Star(Math.random() * sw, Math.random() * sh));
    }
  }

  // Camera zoom curve. Push-in completes in HUSH (before sand particles
  // start so the motion doesn't stutter against the dissolve emissions),
  // holds at peak through the whole symbolic core, and pulls back ONLY
  // after the title + letterbox have unwound — that tail second has no
  // other moving elements, so the zoom-out reads as a clean breath out.
  getCameraZoom() {
    const t = this.t;
    if (t <= 0) return 1;
    if (t < T.HUSH) return 1 + 0.8 * smoothstep(0, T.HUSH, t);
    if (t < T.POST_ANIM) return 1.8;
    return 1.8 - 0.8 * smoothstep(T.POST_ANIM, T.END, t);
  }

  // Render the full overlay. Splits into a world-space pass (particles,
  // halo, ground shockwaves) and a screen-space pass (tint, starfield,
  // mandala, beams, lotus, flash, letterbox, title).
  renderOverlay(r, ctx) {
    const t = this.t;
    const sw = r.width;
    const sh = r.height;
    const zoom = ctx.zoom != null ? ctx.zoom : 1;

    // --- world-space layer (under the camera transform + zoom) ---
    r.save();
    if (zoom !== 1) {
      r.translate(sw / 2, sh / 2);
      r.scale(zoom, zoom);
      r.translate(-sw / 2, -sh / 2);
    }
    if (ctx.camera) ctx.camera.apply(r);

    // Ground shockwaves under the player, drawn before halo + particles
    // so the halo glow sits on top of the rings.
    for (const s of this.shocks) s.render(r);
    if (ctx.player) {
      this._renderPlayerHalo(r, ctx.player.pos.x, ctx.player.pos.y, t);
    }
    for (const e of this.dissolveEmbers) e.render(r);
    for (const s of this.spirals) s.render(r);
    for (const e of this.goldEmbers) e.render(r);

    r.restore();

    // --- screen-space layer ---
    // Player position projected to screen. With zoom-about-center, the
    // post-zoom screen position scales relative to (sw/2, sh/2).
    let px = sw / 2;
    let py = sh / 2;
    if (ctx.player && ctx.camera) {
      const proj = ctx.camera.worldToScreen(ctx.player.pos.x, ctx.player.pos.y);
      px = (proj.x - sw / 2) * zoom + sw / 2;
      py = (proj.y - sh / 2) * zoom + sh / 2;
    }

    this._renderSepiaTint(r, sw, sh, t);
    this._renderDarkness(r, sw, sh, t);
    this._renderStars(r, sw, sh, t);
    this._renderBeam(r, sw, sh, px, t);
    this._renderRadialBeams(r, sw, sh, px, py, t);
    this._renderWheelAndSun(r, sw, sh, px, py, t);
    this._renderLotus(r, sw, sh, px, py, t);
    this._renderFlash(r, sw, sh, t);
    this._renderLetterbox(r, sw, sh, t);
    this._renderTitle(r, sw, sh, t);
  }

  // --- World-space halo: a soft gold disc around the player that builds
  // through dissolution and explodes during lotus. ---
  _renderPlayerHalo(r, x, y, t) {
    // Build-up alpha: faint in P1, climbing through P2, peaks at the
    // singularity (end of P3), holds through P4-P5, fades in P6.
    const buildup = smoothstep(0, T.LIGHT, t) * (1 - smoothstep(T.LOTUS, T.WORD, t));
    if (buildup < 0.02) return;
    // Pulse: three concentric circles with descending alpha.
    const baseR = 24 + 50 * smoothstep(T.HUSH, T.LIGHT, t);
    const layers = [
      { rad: baseR * 1.0, a: 0.55, c: '#fff5d2' },
      { rad: baseR * 1.6, a: 0.30, c: '#ffd76a' },
      { rad: baseR * 2.4, a: 0.15, c: '#f4a72c' },
    ];
    for (const L of layers) {
      r.setAlpha(L.a * buildup);
      r.circle(x, y, L.rad, L.c);
      r.resetAlpha();
    }
  }

  // --- Sepia tint creeping in during hush and dissolution. ---
  _renderSepiaTint(r, sw, sh, t) {
    const a = 0.55 * smoothstep(0, T.DISSOLVE, t) * (1 - smoothstep(T.DESCENT, T.LIGHT, t));
    if (a < 0.01) return;
    r.setAlpha(a);
    r.rect(0, 0, sw, sh, '#3a1f0a');
    r.resetAlpha();
  }

  // --- Hard darkness during descent. The world all but disappears so
  // the wheel can hold center attention. ---
  _renderDarkness(r, sw, sh, t) {
    const fadeIn = smoothstep(T.DISSOLVE, T.DESCENT, t);
    const fadeOut = smoothstep(T.LIGHT, T.LOTUS, t);
    const a = 0.92 * fadeIn * (1 - fadeOut);
    if (a < 0.01) return;
    r.setAlpha(a);
    r.rect(0, 0, sw, sh, '#000000');
    r.resetAlpha();
  }

  // --- Mandala: three concentric rings (32 dots / 16 diamonds / 8
  // spokes), counter-rotating, centered on the player. During the
  // descent it reads as a dharma wheel; during the light burst it
  // ignites into a sun halo (warm palette, larger elements). Same
  // center as the lotus that replaces it so the morph reads as one
  // continuous symbol. ---
  _renderWheelAndSun(r, sw, sh, px, py, t) {
    const wheelIn = smoothstep(T.HUSH + 0.4, T.DESCENT, t);
    const wheelOut = smoothstep(T.LOTUS - 0.2, T.LOTUS + 0.4, t);
    const wheelA = wheelIn * (1 - wheelOut);
    if (wheelA < 0.02) return;

    const ignite = smoothstep(T.DESCENT, T.LIGHT, t);
    const base = Math.min(sw, sh) * (0.20 + 0.07 * ignite);
    // Subtle pulse synced to a steady ~4 Hz heartbeat-rate; keeps the
    // glyph from looking decal-static.
    const pulse = 1 + 0.04 * Math.sin(t * 4);
    const radius = base * pulse;

    const ring = ignite < 0.5 ? '#fff5d2' : '#ffd700';
    const spoke = ignite < 0.5 ? '#e6dab0' : '#ffe680';
    const lineW = 3 + 4 * ignite;

    r.setAlpha(wheelA);

    // Outer rim.
    r.strokeCircle(px, py, radius, ring, lineW);
    // 32 outer-ring dots rotating with the outer angle. Dots grow with
    // ignite to suggest a sun's corona.
    const outerDots = 32;
    const dotSize = 2 + 2 * ignite;
    for (let i = 0; i < outerDots; i++) {
      const a = this._ringAngleOuter + (i * Math.PI * 2) / outerDots;
      const x = px + Math.cos(a) * radius;
      const y = py + Math.sin(a) * radius;
      r.circle(x, y, dotSize, ring);
    }

    // Middle ring: 16 small diamonds counter-rotating. Two triangles
    // form each diamond.
    const midR = radius * 0.78;
    r.strokeCircle(px, py, midR, ring, Math.max(1, lineW * 0.5));
    const midCount = 16;
    const diamond = 4 + 3 * ignite;
    for (let i = 0; i < midCount; i++) {
      const a = this._ringAngleMid + (i * Math.PI * 2) / midCount;
      const x = px + Math.cos(a) * midR;
      const y = py + Math.sin(a) * midR;
      r.triangle(x - diamond, y, x, y - diamond, x + diamond, y, spoke);
      r.triangle(x - diamond, y, x, y + diamond, x + diamond, y, spoke);
    }

    // Inner ring: 8 main spokes from hub. During ignite they extend
    // BEYOND the outer rim as sun rays, making the glyph bloom from a
    // contained wheel into a radiant disc.
    const spokes = 8;
    const innerR = radius * 0.07;
    const spokeOuterR = radius * (0.76 + 0.55 * ignite);
    for (let i = 0; i < spokes; i++) {
      const a = this._ringAngleInner + (i * Math.PI * 2) / spokes;
      const x1 = px + Math.cos(a) * innerR;
      const y1 = py + Math.sin(a) * innerR;
      const x2 = px + Math.cos(a) * spokeOuterR;
      const y2 = py + Math.sin(a) * spokeOuterR;
      r.line(x1, y1, x2, y2, spoke, Math.max(2, lineW * 0.9));
    }

    // Hub: amber center + bright core.
    r.circle(px, py, radius * 0.09, ring);
    r.circle(px, py, radius * 0.05, '#ffffff');
    r.resetAlpha();
  }

  // --- Screen-space starfield: revealed when the world goes dark in
  // the descent phase. Each star twinkles on its own phase. ---
  _renderStars(r, sw, sh, t) {
    if (this.stars.length === 0) return;
    const fadeIn = smoothstep(T.DISSOLVE, T.DESCENT, t);
    const fadeOut = smoothstep(T.LIGHT + 0.2, T.LOTUS, t);
    const masterA = fadeIn * (1 - fadeOut);
    if (masterA < 0.02) return;
    for (const star of this.stars) star.render(r, t, masterA);
  }

  // --- Eight radial beams emanating from the player's screen position
  // at the light moment. Stacked widths (wide-to-narrow) give each
  // beam a soft edge. Rotates slowly with the inner ring so it feels
  // tethered to the mandala. ---
  _renderRadialBeams(r, sw, sh, px, py, t) {
    const a = smoothstep(T.DESCENT, T.LIGHT, t) * (1 - smoothstep(T.LIGHT + 0.4, T.LOTUS, t));
    if (a < 0.02) return;
    const len = Math.max(sw, sh) * 1.4;
    const beams = 8;
    const slabs = [
      { w: 28, c: '#7a5c1a', a: 0.18 },
      { w: 16, c: '#c9931b', a: 0.35 },
      { w: 8,  c: '#ffd700', a: 0.65 },
      { w: 3,  c: '#fff2a8', a: 0.95 },
    ];
    for (let i = 0; i < beams; i++) {
      const angle = (i / beams) * Math.PI * 2 + this._ringAngleInner * 0.3;
      const ex = px + Math.cos(angle) * len;
      const ey = py + Math.sin(angle) * len;
      for (const S of slabs) {
        r.setAlpha(a * S.a);
        r.line(px, py, ex, ey, S.c, S.w);
        r.resetAlpha();
      }
    }
  }

  // --- Letterbox bars. Slide in during HUSH, hold through the heart
  // of the cutscene, slide out at the very end. Adds cinematic frame. ---
  _renderLetterbox(r, sw, sh, t) {
    const slideIn = smoothstep(0, T.HUSH, t);
    // Letterbox unwinds together with the title; the closing zoom-out
    // window stays bar-free so the world reveal isn't constricted.
    const slideOut = smoothstep(T.WORD + 0.3, T.POST_ANIM, t);
    const ratio = slideIn * (1 - slideOut);
    if (ratio < 0.01) return;
    const barH = sh * 0.12 * ratio;
    r.rect(0, 0, sw, barH, '#000000');
    r.rect(0, sh - barH, sw, barH, '#000000');
  }

  // --- Vertical beam piercing from the top of the screen to the
  // player position, peaking at P4 (LIGHT). Procedural gradient via
  // stacked translucent slabs of decreasing width. ---
  _renderBeam(r, sw, sh, px, t) {
    const a = smoothstep(T.DESCENT, T.LIGHT, t) * (1 - smoothstep(T.LOTUS - 0.2, T.WORD, t));
    if (a < 0.01) return;
    // Six slabs, widest outermost, narrowest brightest at the core.
    const slabs = [
      { w: 110, c: '#3a2a08', a: 0.18 },
      { w: 72,  c: '#7a5c1a', a: 0.30 },
      { w: 46,  c: '#c9931b', a: 0.45 },
      { w: 28,  c: '#ffd700', a: 0.65 },
      { w: 14,  c: '#fff2a8', a: 0.85 },
      { w: 5,   c: '#ffffff', a: 1.00 },
    ];
    for (const S of slabs) {
      r.setAlpha(a * S.a);
      r.rect(px - S.w / 2, 0, S.w, sh, S.c);
      r.resetAlpha();
    }
  }

  // --- Lotus / rose. 8 petals staggered open around the player's
  // screen position. Each petal is a thin elongated triangle pointing
  // outward; opens by scaling its length from 0 to full. ---
  _renderLotus(r, sw, sh, px, py, t) {
    const a = smoothstep(T.LIGHT - 0.1, T.LIGHT + 0.4, t) * (1 - smoothstep(T.WORD - 0.2, T.WORD + 0.3, t));
    if (a < 0.02) return;

    const baseRadius = Math.min(sw, sh) * 0.22;
    const petals = 8;
    // Each petal opens with a staggered delay so the bloom feels alive.
    const stagger = 0.07;
    const openStart = T.LIGHT;
    r.setAlpha(a);

    // Two layers: a translucent outer halo behind the petals + the
    // sharp petals on top. Halo gives a sense of glow.
    r.setAlpha(a * 0.35);
    r.circle(px, py, baseRadius * 1.1, '#fff2a8');
    r.resetAlpha();

    r.setAlpha(a);
    for (let i = 0; i < petals; i++) {
      const open = clamp01((t - (openStart + i * stagger)) / 0.45);
      if (open <= 0) continue;
      const len = baseRadius * open;
      const half = baseRadius * 0.18 * open;
      const angle = (i * Math.PI * 2) / petals - Math.PI / 2;
      const tipX = px + Math.cos(angle) * len;
      const tipY = py + Math.sin(angle) * len;
      const perpX = -Math.sin(angle);
      const perpY = Math.cos(angle);
      const baseX = px + Math.cos(angle) * (baseRadius * 0.08);
      const baseY = py + Math.sin(angle) * (baseRadius * 0.08);
      const leftX = baseX + perpX * half;
      const leftY = baseY + perpY * half;
      const rightX = baseX - perpX * half;
      const rightY = baseY - perpY * half;
      const col = i % 2 === 0 ? '#ffd700' : '#fff2a8';
      r.triangle(leftX, leftY, rightX, rightY, tipX, tipY, col);
    }
    // Central core after petals are mostly open.
    const coreA = smoothstep(T.LIGHT + 0.4, T.LIGHT + 0.9, t);
    r.setAlpha(a * coreA);
    r.circle(px, py, baseRadius * 0.16, '#ffffff');
    r.resetAlpha();
  }

  // --- White flash at the singularity moment, plus a softer flash when
  // the lotus opens. Short, sharp — masks visual transitions. ---
  _renderFlash(r, sw, sh, t) {
    // Two flashes: one at the end of P3 (impact), one at P5 (bloom).
    let a = 0;
    if (t > T.DESCENT - 0.08 && t < T.LIGHT) {
      const f = 1 - Math.abs((t - T.DESCENT) / 0.16);
      a = Math.max(a, clamp01(f) * 0.85);
    }
    if (t > T.LIGHT - 0.05 && t < T.LIGHT + 0.45) {
      const f = 1 - Math.abs((t - (T.LIGHT + 0.05)) / 0.25);
      a = Math.max(a, clamp01(f) * 0.55);
    }
    if (a < 0.01) return;
    r.setAlpha(a);
    r.rect(0, 0, sw, sh, '#ffffff');
    r.resetAlpha();
  }

  // --- Title cluster: big title + aphorism + tail. Grows in during
  // WORD phase, holds, and fades before POST_ANIM so the closing zoom
  // tail plays against an empty screen. ---
  _renderTitle(r, sw, sh, t) {
    const showA = smoothstep(T.LOTUS, T.WORD, t) * (1 - smoothstep(T.WORD + 0.3, T.POST_ANIM, t));
    if (showA < 0.02) return;

    const cx = sw / 2;
    const grow = smoothstep(T.LOTUS, T.WORD, t);
    // Title sizing: target height scales with viewport, but RU
    // "ПЕРЕРОЖДЕНИЕ" is 12 wide chars, so the renderer's auto-fit shrinks
    // to maxWidth = 85% of screen so edge letters never clip.
    const titleSize = Math.floor(Math.min(96, sw / 9) * (0.6 + 0.4 * grow));
    const maxTitleWidth = sw * 0.85;
    const aphSize = Math.max(18, Math.floor(titleSize / 2.6));
    const tailSize = Math.max(14, Math.floor(titleSize / 4));

    const cy = sh / 2;

    r.setAlpha(showA);
    r.textOutlinedFit(
      i18n('cutscene.rebirth.title'),
      cx, cy - titleSize * 0.65,
      maxTitleWidth,
      '#ffd700', '#4a2a00', titleSize, 'center', 'middle',
    );
    r.textOutlined(
      i18n('cutscene.rebirth.aphorism'),
      cx, cy + titleSize * 0.05,
      '#ffeebb', '#2a1500', aphSize, 'center', 'middle',
    );
    r.textOutlined(
      i18n('cutscene.rebirth.tail', { wave: this.waveBefore }),
      cx, cy + titleSize * 0.65,
      '#fff5d2', '#000', tailSize, 'center', 'middle',
    );
    r.resetAlpha();
  }
}

// Local alias to avoid colliding with the `t` parameter used as time in
// many render helpers above. The imported i18n function is still `t`,
// but inside helpers we use `i18n(...)` for clarity.
function i18n(key, params) { return t(key, params); }
